import React, {
  createContext,
  useCallback,
  useContext,
  useReducer,
} from "react";

import dfAddDays from "date-fns/addDays";
import dfSubDays from "date-fns/subDays";

import { formatDate, getDateByTimeZone } from "@/lib/date";

import SentBackDelivery from "@/Models/SentBackDelivery";
import api from "../Api";

export const QUERY_SORT = {
  SENT_BACK_AT: "sentBackAt",
  COMMENT_CREATED_AT: "commentCreatedAt",
  UNCONFIRMED_COMMENT: "unconfirmedComment",
};

export const CONSTANTS = {
  FETCH_ALL: "FETCH_ALL",
  SET_QUERY: "SET_QUERY",
};

const zonedNow = getDateByTimeZone();
const initDateFrom = formatDate(dfSubDays(zonedNow, 1));
const initDateTo = formatDate(dfAddDays(zonedNow, 1));

const INITIAL_QUERY = {
  sentBackAtFrom: initDateFrom,
  sentBackAtTo: initDateTo,
  bookId: "",
  receiverName: "",
  receiverMobile: "",
  page: 1,
  pageSize: 20,
  sort: QUERY_SORT.SENT_BACK_AT,
};

const INITIAL_STATE = {
  rows: [],
  totalCount: 0,
  pageCount: 0,
  query: INITIAL_QUERY,
};

const reducer = (state, action) => {
  switch (action.type) {
    case CONSTANTS.FETCH_ALL:
      return {
        ...state,
        rows: action.rows,
        totalCount: action.totalCount,
        pageCount: action.pageCount,
      };
    case CONSTANTS.SET_QUERY:
      return {
        ...state,
        query: {
          ...state.query,
          ...action.query,
        },
      };
    default:
      return INITIAL_STATE;
  }
};

const compactQuery = (query = {}) => {
  const next = {};

  Object.keys(query).forEach((key) => {
    const value = query[key];

    if (value === undefined || value === null || value === "") {
      return;
    }

    next[key] = value;
  });

  return next;
};

export const Context = createContext(INITIAL_STATE);

export const Provider = ({ children }) => {
  const [state, dispatch] = useReducer(reducer, INITIAL_STATE);

  return (
    <Context.Provider value={{ dispatch, state }}>{children}</Context.Provider>
  );
};

export const useSentBackDeliveriesStore = () => {
  const { dispatch, state } = useContext(Context);

  const fetchAll = useCallback(
    async (queryOverride) => {
      const query = compactQuery(queryOverride || state.query);
      const { rows, totalCount } = await api.get(
        "/sent-back-deliveries",
        query,
      );
      const items = (rows || []).map((d) => new SentBackDelivery(d));

      dispatch({
        type: CONSTANTS.FETCH_ALL,
        rows: items,
        totalCount: totalCount || 0,
        pageCount:
          Math.floor((totalCount || 0) / (query.pageSize || 20)) +
          ((totalCount || 0) % (query.pageSize || 20) ? 1 : 0),
      });
    },
    [dispatch, state.query],
  );

  const setQuery = useCallback(
    (query = {}) => {
      dispatch({ type: CONSTANTS.SET_QUERY, query });
    },
    [dispatch],
  );

  return {
    state,
    fetchAll,
    setQuery,
  };
};
